import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  FaCalendarAlt,
  FaExclamationCircle,
  FaMoneyBillWave,
  FaUserPlus,
  FaUserFriends,
  FaTasks,
  FaEnvelopeOpenText,
} from 'react-icons/fa';
import { getInboxInfo, InboxInfo } from '../../services/notificationService';
import { setSelectedCommunity } from '../../redux/slices/applicationSlice';
import { InboxItem } from '../../types/inbox';

type InboxAny = Omit<InboxItem, 'type'> & { type?: string } & Record<string, any>;

export default function OpenActionsCard({
  inboxInfo: inboxInfoProp,
  hideViewLink = false,
  itemsLimit = 5,
  onItemClick,
  filterTypes,
}: {
  inboxInfo?: InboxInfo | null;
  hideViewLink?: boolean;
  itemsLimit?: number | null;
  onItemClick?: (item: InboxAny) => void;
  filterTypes?: string[] | null;
}) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [inboxInfo, setInboxInfo] = useState<InboxInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const formatDate = (iso?: string) => {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleDateString(i18n.language, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (e) {
      return iso.toString();
    }
  };

  useEffect(() => {
    let cancelled = false;

    if (inboxInfoProp) {
      setInboxInfo(inboxInfoProp);
      setLoading(false);
      return () => {
        cancelled = true;
      };
    }

    setLoading(true);
    getInboxInfo()
      .then((data) => {
        if (!cancelled) setInboxInfo(data);
      })
      .catch((err) => {
        console.error('Failed to load inbox info', err);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [inboxInfoProp]);

  const allItems = useMemo(() => {
    if (!inboxInfo) return [];

    const collect = (items: InboxAny[] | undefined) => (Array.isArray(items) ? items : []);

    const arr: InboxAny[] = [
      ...collect(inboxInfo.introductions),
      ...collect(inboxInfo.dataRequests),
      ...collect(inboxInfo.communityRequests),
      ...collect(inboxInfo.connectionInvites),
      ...collect(inboxInfo.eventInvites),
      ...collect(inboxInfo.communityInvites),
      ...collect(inboxInfo.tasks),
      ...collect(inboxInfo.communityChangedDataRequests),
      ...collect(inboxInfo.paymentRequests),
    ];

    let filtered = arr;
    if (filterTypes && filterTypes.length) {
      const set = new Set(filterTypes);
      filtered = arr.filter((i) => set.has(i.type || ''));
    }

    return filtered
      .slice()
      .sort(
        (a: InboxAny, b: InboxAny) => new Date(String(b.createdAt)).getTime() - new Date(String(a.createdAt)).getTime(),
      );
  }, [inboxInfo, filterTypes]);

  const itemsToShow = itemsLimit === null ? allItems : allItems.slice(0, itemsLimit);

  const renderMessage = (item: InboxAny) => {
    if (item.type) {
      const key = `inboxScreen.inboxMessage.${item.type}`;
      const args: Record<string, any> = {};

      switch (item.type) {
        case 'INTRODUCTION':
          args.name = item.fromAlias || item.name || '';
          args.contactName = item.toAlias || item.toName || '';
          break;
        case 'DATA_REQUEST':
          args.name = item.fromAlias || item.requesterName || '';
          args.data = Array.isArray(item.requestedProps) ? item.requestedProps.join(', ') : item.requestedProps || '';
          break;
        case 'COMMUNITY_REQUEST':
          args.name = item.fromAlias || '';
          args.communityName = item.communityName || '';
          break;
        case 'CONNECTION_INVITE':
          args.name = item.fromAlias || item.name || '';
          break;
        case 'EVENT_INVITE':
          args.name = item.fromAlias || item.name || '';
          args.eventTitle = item.eventTitle || '';
          args.dateString = item.dateString || '';
          args.timeString = item.timeString || '';
          break;
        case 'COMMUNITY_INVITE':
          args.name = item.fromAlias || '';
          args.communityName = item.communityName || '';
          break;
        case 'TASK':
          args.taskName = item.taskName || item.taskDescription || '';
          args.taskDescription = item.taskDescription || item.taskName || '';
          args.communityName = item.fromAlias || item.communityName || '';
          args.taskDueDate = item.taskDueDate ? formatDate(item.taskDueDate) : '';
          break;
        case 'DASHBOARD_CHANGED_DATA_REQUEST':
          args.communityName = item.communityName || '';
          break;
        case 'PAYMENT_REQUEST':
        case 'P2B_PAYMENT':
          args.amount = item.amount
            ? (item.amount / 100).toFixed(2)
            : item.amountInCents
              ? (item.amountInCents / 100).toFixed(2)
              : '';
          args.name = item.fromAlias || item.name || '';
          break;
        default:
          break;
      }

      const translated = t(key, args);
      if (translated !== key) return translated;
    }

    if (item.eventTitle) return t('page.dashboard.actionMessage.eventInvite', { title: item.eventTitle });
    if (item.taskDescription) return t('page.dashboard.actionMessage.task', { description: item.taskDescription });
    if (item.amount || item.amountInCents)
      return t('page.dashboard.actionMessage.paymentRequest', { name: item.fromAlias || item.requesterName || '' });
    if (item.requestedProps)
      return t('page.dashboard.actionMessage.dataRequest', { name: item.fromAlias || item.requesterName || '' });
    if (item.communityName && item.businessInviteLinkId)
      return t('page.dashboard.actionMessage.communityInvite', { community: item.communityName });
    if (item.communityName && !item.businessInviteLinkId)
      return t('page.dashboard.actionMessage.communityRequest', {
        name: item.fromAlias || '',
        community: item.communityName,
      });
    if (item.toAlias && item.fromAlias)
      return t('page.dashboard.actionMessage.introduction', { name: item.fromAlias, introduced: item.toAlias });

    if (item.title) return item.title;

    return item.description || item.name || item.fromAlias || t('page.dashboard.actionMessage.default');
  };

  const renderSecondary = (item: InboxAny) => {
    return item.fromAlias ? item.fromAlias : '';
  };

  const timeString = (iso?: string | number | Date) => {
    if (!iso) return '';
    try {
      return new Date(iso as any).toLocaleString();
    } catch (e) {
      return String(iso);
    }
  };

  const handleItemClick = (item: InboxAny) => {
    if (onItemClick) {
      onItemClick(item);
      return undefined;
    }

    try {
      switch (item?.type) {
        case 'EVENT_INVITE':
          if (item.entityId) navigate('/agenda', { state: { openEventId: item.entityId } });
          else if (item.eventId) navigate(`/agenda/${item.eventId}`);
          else navigate('/agenda');
          break;
        case 'CONNECTION_INVITE':
          if (item.entityId) navigate(`/connectionInvite?inviteId=${encodeURIComponent(item.entityId)}`);
          else navigate('/inbox');
          break;
        case 'COMMUNITY_INVITE':
          if (item.businessInviteLinkId)
            navigate(`/moments/invite?link=${encodeURIComponent(item.businessInviteLinkId)}`);
          else if (item.entityId) navigate(`/moments/invite?invitationId=${encodeURIComponent(item.entityId)}`);
          else navigate('/communities');
          break;
        case 'DATA_REQUEST':
        case 'DASHBOARD_CHANGED_DATA_REQUEST':
          if (item.entityId) navigate(`/data-request?invitationId=${encodeURIComponent(item.entityId)}`);
          else navigate('/inbox');
          break;
        case 'TASK': {
          // Resolve community id from multiple potential sources on the inbox item
          const communityId = item.communityId || item.fromId || (item.community && item.community.id);
          if (communityId) {
            try {
              dispatch(setSelectedCommunity(Number(communityId)));
            } catch (e) {
              /* ignore */
            }
          }

          const navState: Record<string, any> = {};
          if (item.entityId) navState.openTaskId = item.entityId;
          if (communityId) navState.communityId = Number(communityId);

          navigate('/community/tasks', { state: navState });
          break;
        }
        case 'PAYMENT_REQUEST':
        case 'P2B_PAYMENT':
          const vera = item.veraLink || item.veraId || item.entityId;
          if (vera) navigate(`/payment?veraId=${encodeURIComponent(vera)}`);
          else navigate('/inbox');
          break;
        case 'INTRODUCTION':
          navigate('/introductions');
          break;
        default:
          navigate('/inbox');
      }
    } catch (err) {
      console.error('Failed to navigate for inbox item', err);
      navigate('/inbox');
    }

    return undefined;
  };

  return (
    <div className="overflow-hidden rounded-xl border shadow-sm">
      <div className="flex items-center justify-between rounded-t-xl bg-secondary-200 px-4 py-2">
        <div>
          <h3 className="text-secondary-900 font-semibold">
            {t('page.dashboard.actionsTitle') || t('common.success.stats.actions')}
          </h3>
        </div>
        <div className="flex items-center gap-3">
          {filterTypes && filterTypes.length ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary">
              {t('page.inbox.filtered') || 'Filtered'}
            </span>
          ) : null}

          {!hideViewLink && (
            <p
              onClick={() => navigate('/inbox')}
              className="flex cursor-pointer items-center gap-2 text-sm font-medium text-details/60">
              {t('general.view')}
            </p>
          )}
        </div>
      </div>

      <div className="p-4">
        {loading ? (
          <div className="text-secondary-500 py-6 text-center">
            <p className="text-sm">{t('general.loading')}</p>
          </div>
        ) : itemsToShow.length === 0 ? (
          <div className="text-secondary-500 py-6 text-center">
            <p className="text-sm">
              {filterTypes && filterTypes.length
                ? t('page.inbox.noResultsForFilters') || 'No items match the selected filters'
                : t('page.dashboard.noNotifications') || t('general.noResults')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {itemsToShow.map((item: InboxAny, idx: number) => (
              <button
                type="button"
                key={idx}
                onClick={() => handleItemClick(item)}
                className="hover:bg-secondary-100 flex w-full transform items-center gap-3 rounded-lg bg-secondary-50 p-3 text-left transition duration-150 ease-in-out hover:-translate-y-1 hover:shadow-sm">
                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-primary/5 text-primary">
                  {(() => {
                    switch (item.type) {
                      case 'EVENT_INVITE':
                        return <FaCalendarAlt className="h-6 w-6" />;
                      case 'PAYMENT_REQUEST':
                      case 'P2B_PAYMENT':
                        return <FaMoneyBillWave className="h-6 w-6" />;
                      case 'CONNECTION_INVITE':
                        return <FaUserPlus className="h-6 w-6" />;
                      case 'COMMUNITY_INVITE':
                      case 'COMMUNITY_REQUEST':
                        return <FaUserFriends className="h-6 w-6" />;
                      case 'TASK':
                        return <FaTasks className="h-6 w-6" />;
                      case 'DATA_REQUEST':
                      case 'DASHBOARD_CHANGED_DATA_REQUEST':
                        return <FaEnvelopeOpenText className="h-6 w-6" />;
                      default:
                        return <FaExclamationCircle className="h-6 w-6" />;
                    }
                  })()}
                </div>

                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-primary">{renderMessage(item)}</p>
                  <p className="truncate text-sm text-details/60">{renderSecondary(item)}</p>
                </div>

                <div className="whitespace-nowrap text-xs text-details/60">{timeString(item.createdAt)}</div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
